import { CommonModule } from '@angular/common';
import { Component, inject, Signal, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { PartnerHealthsService } from '@galaxy/partner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PartnerHealthState, PartnerHealthSubState, PartnerModelApiService } from '@vendasta/partner';
import { combineLatest, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { CompanyAccountService } from '../../../company-account.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EditPartnerIdDialogComponent } from './edit-partner-id-dialog/edit-partner-id-dialog.component';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

interface PartnerInformation {
  partnerId: string;
  name: string;
  accountGroupId: string;
  subscriptionTierId: string;
  health: {
    state: PartnerHealthState;
    subState: PartnerHealthSubState;
    displayState: string;
    displaySubState: string;
    iconCss: string;
  };
}

interface CompanyWithoutPartnerInfo {
  accountGroupId: string;
  name: string;
}

const companyPartnerFieldId = 'FieldID-6f2472c8-3401-4797-9db1-90fe524bd9a8';
const companyNameFieldId = 'FieldID-b2a5d86e-f9ae-11ed-be56-0242ac120002';
const accountGroupId = 'FieldID-12c064c5-3260-478e-952d-04a3a7e83b15';

@Component({
  selector: 'app-partner-info',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatButtonModule,
    MatIconModule,
    GalaxyButtonLoadingIndicatorModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyButtonGroupModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
  ],
  templateUrl: './partner-info.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss', './partner-info.component.scss'],
})
export class PartnerInfoComponent {
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  protected companyPartnerInfo$: Observable<PartnerInformation>;
  protected createPartnerButtonLoading$ = this.accountCreationService.createAccountGroupLoading$(this.data.company_id);
  protected isLoading = signal(true);
  protected partnerInfo: Signal<PartnerInformation>;
  protected companyInfo$: Observable<CompanyWithoutPartnerInfo>;
  protected companyInfo: Signal<CompanyWithoutPartnerInfo>;

  constructor(
    private readonly partnerModelService: PartnerModelApiService,
    private router: Router,
    private readonly accountCreationService: CompanyAccountService,
    private readonly partnerHealthsService: PartnerHealthsService,
    private environmentService: EnvironmentService,
    private readonly snackbarService: SnackbarService,
    private clipboard: Clipboard,
    private dialog: MatDialog,
  ) {
    this.setupCompanyInfoFetching();
    this.companyInfo = toSignal(this.companyInfo$);
    this.partnerInfo = toSignal(this.companyPartnerInfo$);
  }

  private setupCompanyInfoFetching(): void {
    const company$ = this.accountCreationService.getCompany$(this.data.company_id);

    const companyPID$ = company$.pipe(
      map((company) => {
        return company.fields?.find((field) => field.fieldId === companyPartnerFieldId)?.stringValue;
      }),
    );
    const companyName$ = company$.pipe(
      map((company) => {
        return company.fields?.find((field) => field.fieldId === companyNameFieldId)?.stringValue;
      }),
    );
    const accountGroupId$ = company$.pipe(
      map((company) => {
        return company.fields?.find((field) => field.fieldId === accountGroupId)?.stringValue;
      }),
    );

    const subscriptionTierId$ = companyPID$.pipe(
      switchMap((pid) => {
        if (!pid) {
          return of(null);
        }
        return this.partnerModelService.getPartnerModel({ partnerId: pid });
      }),
      map((resp) => {
        return resp?.partner?.subscriptionTierId;
      }),
    );

    const financialHealth$ = companyPID$.pipe(
      switchMap((pid) => {
        if (!pid) {
          return of(null);
        }
        return this.partnerHealthsService.get(pid);
      }),
      map((resp) => {
        return resp;
      }),
    );

    this.companyInfo$ = combineLatest([accountGroupId$, companyName$]).pipe(
      map(([accountGroupId, companyName]) => {
        return {
          accountGroupId: accountGroupId,
          name: companyName,
        };
      }),
    );

    this.companyPartnerInfo$ = combineLatest([
      companyPID$,
      companyName$,
      accountGroupId$,
      subscriptionTierId$,
      financialHealth$,
    ]).pipe(
      map(([pid, name, accountGroupId, tierId, health]) => {
        if (!pid || !tierId) {
          return null;
        }
        const display = this.displayTextAndIcon(health);
        return <PartnerInformation>{
          partnerId: pid,
          subscriptionTierId: tierId,
          name: name,
          accountGroupId: accountGroupId,
          health: {
            state: health?.state,
            subState: health?.subState,
            displayState: display[0],
            displaySubState: display[2],
            iconCss: display[1],
          },
        };
      }),
      tap(() => this.isLoading.set(false)),
    );
  }

  createPartner(): void {
    if (!this.data.company_id) {
      this.snackbarService.openErrorSnack('CRM.ERROR.NO_COMPANY_ID');
      return;
    }

    firstValueFrom(this.accountCreationService.fetchAccountGroupId$(this.data.company_id))
      .then((accountGroupId) => {
        if (accountGroupId) {
          this.router.navigateByUrl(
            `/superadmin/partner/create?fromAccountGroup=${accountGroupId}&companyID=${this.data.company_id}`,
          );
        } else {
          this.snackbarService.openErrorSnack('CRM.ERROR.NAVIGATE_TO_PAGE_FAILED');
        }
      })
      .catch((err) => {
        console.error(err);
        this.snackbarService.openErrorSnack('CRM.ERROR.NAVIGATE_TO_PAGE_FAILED');
      });
  }

  private displayTextAndIcon(health): string[] {
    let subStateDisplay = '';
    switch (health?.subState) {
      case 0:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.UNSPECIFIED';
        break;
      case 1:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.ACTIVE';
        break;
      case 2:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.OVERDUE';
        break;
      case 3:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.SUSPENDED';
        break;
      case 4:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.PAYMENT_PLAN';
        break;
      default:
        subStateDisplay = 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.UNSPECIFIED';
        break;
    }

    switch (health?.state) {
      case 0:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.HEALTHY',
          'partner-health-token-healthy',
          subStateDisplay,
        ];
      case 1:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.AT_RISK',
          'partner-health-token-risk',
          subStateDisplay,
        ];
      case 2:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.RESTRICTED',
          'partner-health-token-restricted',
          subStateDisplay,
        ];
      default:
        return [
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.HEALTH_STATE.HEALTHY',
          'partner-health-token-healthy',
          subStateDisplay,
        ];
    }
  }

  goToPricing(): void {
    this.companyPartnerInfo$
      .pipe(
        take(1),
        map((partnerInfo) => {
          let url = '';
          if (this.environmentService.getEnvironment() === Environment.PROD) {
            url = 'https://billing-prod.apigateway.co/merchants/' + partnerInfo.partnerId;
          } else {
            url = 'https://billing-demo.apigateway.co/merchants/' + partnerInfo.partnerId;
          }
          window.open(url, '_blank');
        }),
      )
      .subscribe();
  }

  copyToClipboard(name: string): void {
    this.clipboard.copy(name);
    this.snackbarService.openSuccessSnack('COMMON.SNACKBAR_MESSAGE.COPY_SUCCESS', { action: 'Close' });
  }
  editPartnerID(pid: string, name: string, accountGroupId: string): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '550px';
    dialogConfig.minHeight = '300px';
    dialogConfig.data = {
      pid: pid,
      name: name,
      accountGroupId: accountGroupId,
    };
    const dialogRef = this.dialog.open(EditPartnerIdDialogComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((newPartnerId) => {
        if (newPartnerId) {
          this.companyPartnerInfo$
            .pipe(
              take(1),
              map((companyInfo) => {
                return {
                  ...companyInfo,
                  partnerId: newPartnerId,
                  name: name,
                  accountGroupId: accountGroupId,
                };
              }),
              tap((updatedInfo) => {
                this.partnerInfo = signal(updatedInfo);
              }),
            )
            .subscribe();
        }
      });
  }
}
